import {
  createContext,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Asset, Collection } from "../generated/gql/graphql";
import { emptyFn } from "../utils/provider";

type AssetListContextType = {
  selectedAllAssets: boolean;
  selectedFiles: Asset[];
  setSelectedFile(file: Asset, checked: boolean): void;
  selectAll(): void;
  clearSelection(): void;
};

export const AssetListContext = createContext<AssetListContextType>({
  selectedAllAssets: false,
  selectedFiles: [],
  setSelectedFile: emptyFn,
  selectAll: emptyFn,
  clearSelection: emptyFn,
});

type Props = {
  initAssets: Asset[];
  collection?: Collection;
  children: ReactNode | ReactElement | ReactElement[];
};

export default function AssetListProvider(props: Props): ReactElement {
  const { initAssets, children } = props;

  const [loadedAssets, setLoadedAssets] = useState(initAssets);
  // const [loadedCollection, setLoadedCollection] = useState(collection);

  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);

  useEffect(() => {
    setLoadedAssets(initAssets);
  }, [initAssets]);

  function internalSetSelectedFile(asset: Asset, checked: boolean): void {
    setSelectedAssets([...selectedAssets, asset]);

    if (!checked) {
      setSelectedAssets(selectedAssets.filter((item) => item.id !== asset.id));
    }
  }

  function selectAll(): void {
    setSelectedAssets(loadedAssets);
  }

  function clearSelection(): void {
    setSelectedAssets([]);
  }

  const values = useMemo(
    () => ({
      selectedFiles: selectedAssets,
      setSelectedFile: internalSetSelectedFile,
      selectAll,
      clearSelection,
      selectedAllAssets:
        selectedAssets.length === loadedAssets.length &&
        selectedAssets.length !== 0,
    }),
    [selectedAssets, initAssets, loadedAssets]
  );

  return (
    <AssetListContext.Provider value={values}>
      {children}
    </AssetListContext.Provider>
  );
}
